import { useEffect, useContext, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  PaginationContext,
  SortContext,
  SearchContext,
  ModalContext,
} from "dash-ui";
import {
  UnitFragment,
  useGetUnitsQuery,
  useDeleteUnitMutation,
  GetUnitsDocument,
  useEditUnitMutation,
  useDuplicateUnitMutation,
} from "gql/generated.graphql";
import PropertyInventoryTableUI from "./PropertyInventoryTableUI";
import { AddFloorPlanModal } from "./components/AddFloorPlanModal";
import { InventoryTypeEnum } from "dash-common";
import DeleteInventoryModal from "../DeleteInventoryModal/DeleteInventoryModal";

function PropertiesTable({ filter }: { filter: { propertyId: string } }) {
  const [unitIdToUpdate, setUnitIdToUpdate] = useState<string | null>(null);
  const { propertyId } = filter;
  const { t } = useTranslation("tables", {
    keyPrefix: "inventory",
  });
  const { t: tPlaceholders } = useTranslation("common", {
    keyPrefix: "placeholders",
  });
  const {
    setTotal,
    state: paginationState,
    resetPagination,
    getRefetchProps,
  } = useContext(PaginationContext);
  const { searchSlug } = useContext(SearchContext);
  const { state: sort } = useContext(SortContext);
  const { toggleModal, setPayload } = useContext(ModalContext);

  const { skip, limit } = getRefetchProps(paginationState);
  const searchByName = searchSlug.length > 2 ? searchSlug : null;

  const {
    data: result,
    refetch,
    loading,
  } = useGetUnitsQuery({
    variables: {
      sort: [sort],
      pagination: { skip, limit },
      filter: { propertyId },
    },
  });

  const { data, total } = useMemo(
    () => ({
      data: result?.getUnits?.collection || [],
      total: result?.getUnits?.totalCount || 0,
    }),
    [result]
  );

  const [editUnit, { loading: editLoading }] = useEditUnitMutation({
    refetchQueries: [GetUnitsDocument],
  });

  const [duplicateUnit, { loading: duplicateLoading }] =
    useDuplicateUnitMutation({
      refetchQueries: [GetUnitsDocument],
    });

  useEffect(() => {
    setTotal(total);
  }, [total]);

  // FIXME: re-creating context functions everytime state change. Use useCallback
  useEffect(() => {
    resetPagination();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchSlug]);

  // FIXME: re-creating context functions everytime state change. Use useCallback
  useEffect(() => {
    refetch({
      sort: [sort],
      pagination: { skip, limit },
      filter: { propertyId },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [skip, limit, sort, searchSlug]);

  return (
    <>
      <DeleteInventoryModal />
      <AddFloorPlanModal
        unitId={unitIdToUpdate}
        onClose={() => setUnitIdToUpdate(null)}
      />
      <PropertyInventoryTableUI
        data={data}
        loading={loading || editLoading || duplicateLoading}
        handleChangeType={(
          inventoryType: InventoryTypeEnum,
          unit: UnitFragment
        ) => {
          const rest = {
            floorPlanFileId: unit.floorPlanFileId,
            name: unit.name,
            averageBedroomsNumber: {
              min: unit.averageBedroomsNumber?.min,
              max: unit.averageBedroomsNumber?.max,
            },
            averageBathroomsNumber: {
              min: unit.averageBathroomsNumber?.min,
              max: unit.averageBathroomsNumber?.max,
            },
            averageTotalSqft: {
              min: unit.averageTotalSqft?.min,
              max: unit.averageTotalSqft?.max,
            },
            averageInteriorSqft: {
              min: unit.averageInteriorSqft?.min,
              max: unit.averageInteriorSqft?.max,
            },
            averageExteriorSqft: {
              min: unit.averageExteriorSqft?.min,
              max: unit.averageExteriorSqft?.max,
            },
            startingPrice: unit.startingPrice,
            highestPrice: unit.highestPrice,
            customRooms: unit.customRooms,
          };

          editUnit({
            variables: {
              input: {
                unitDetails: {
                  ...rest,
                  inventoryType,
                },
                unitId: unit._id,
              },
            },
          });
        }}
        handleEditClick={(original: UnitFragment) => {
          toggleModal(true);
          setPayload(original);
        }}
        handleAddClick={(original: UnitFragment) => {
          setUnitIdToUpdate(original._id);
        }}
        handleDeleteClick={(original: UnitFragment) => {
          setPayload(original);
          toggleModal(true, "DeleteInventoryModal");
        }}
        handleCopyClick={(original: UnitFragment) => {
          duplicateUnit({ variables: { unitId: original._id } });
        }}
        placeholder={
          (searchByName ? tPlaceholders`noSearchResults` : t`placeholder`) || ""
        }
      />
    </>
  );
}

export default PropertiesTable;
